import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { type FunctionComponent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import type { JobArchitecturePath } from '~/shared/data-contract/job-architecture';

import type { EntityIdentifier } from '../../data-contract/chat';
import { jobArchitectureKeys } from '../../job-architecture-v2/api/query-keys';
import { useJobArchitectureItemPath } from '../../job-architecture-v2/api/use-job-architecture-item-path';
import { useJobItemVersions } from '../../job-architecture-v2/api/use-job-item-versions';
import {
    buildJobLevelParams,
    buildJobRoleParams,
} from '../../job-architecture-v2/job-profile-standalone/hooks/use-locate-job-architecture-item';

type ItemType = 'job-level' | 'job-role';

type ResolvedEntity = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: ItemType;
};

const resolveEntity = (entity: EntityIdentifier): ResolvedEntity | null => {
    if (entity.type !== 'role' && entity.type !== 'level') return null;
    if (!is.nonEmptyString(entity.id) || !is.nonEmptyString(entity.jobArchitectureId)) return null;
    return {
        architectureId: entity.jobArchitectureId,
        itemId: entity.id,
        itemType: entity.type === 'role' ? 'job-role' : 'job-level',
    };
};

const buildHref = (
    architectureId: string,
    itemId: string,
    itemType: ItemType,
    path: JobArchitecturePath,
    version: number,
): string | null => {
    if (!is.nonEmptyString(path.family) || !is.nonEmptyString(path.cluster)) return null;
    if (itemType === 'job-level' && !is.nonEmptyString(path.role)) return null;

    const builderPath = {
        cluster: path.cluster,
        family: path.family,
        role: itemType === 'job-role' ? itemId : path.role,
    };
    const params =
        itemType === 'job-role'
            ? buildJobRoleParams(architectureId, itemId, builderPath, new URLSearchParams(), undefined, version)
            : buildJobLevelParams(architectureId, itemId, builderPath, new URLSearchParams(), undefined, version);
    return `/job-architecture-v2/${architectureId}/overview?${params.toString()}`;
};

const pickLatestVersion = (versions: readonly { readonly version: number }[]): number =>
    versions.reduce((max, v) => (v.version > max ? v.version : max), 0);

const versionsKeyFor = (architectureId: string, itemId: string, itemType: ItemType): readonly unknown[] =>
    itemType === 'job-role'
        ? jobArchitectureKeys.roleVersions(architectureId, itemId)
        : jobArchitectureKeys.levelVersions(architectureId, itemId);

type RedirectInnerProps = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: ItemType;
};

const RedirectToLatestVersion: FunctionComponent<RedirectInnerProps> = ({ architectureId, itemId, itemType }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const hasNavigatedRef = useRef(false);
    const mountedAtRef = useRef(Date.now());

    // biome-ignore lint/correctness/useExhaustiveDependencies: clear cache once per mount.
    useEffect(() => {
        void queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.itemPath(architectureId, itemId) });
        void queryClient.invalidateQueries({ queryKey: versionsKeyFor(architectureId, itemId, itemType) });
    }, []);

    const pathQuery = useJobArchitectureItemPath(architectureId, itemId);
    const versionsQuery = useJobItemVersions({ architectureId, itemId, itemType });

    const path = pathQuery.data;
    const versions = versionsQuery.data;
    const pathFreshAt = pathQuery.dataUpdatedAt;
    const versionsFreshAt = versionsQuery.dataUpdatedAt;

    useEffect(() => {
        if (hasNavigatedRef.current) return;
        if (is.nullOrUndefined(path) || is.nullOrUndefined(versions) || versions.length === 0) return;
        if (pathFreshAt < mountedAtRef.current || versionsFreshAt < mountedAtRef.current) return;

        const href = buildHref(architectureId, itemId, itemType, path, pickLatestVersion(versions));
        if (is.null(href)) return;

        hasNavigatedRef.current = true;
        navigateRef.current(href);
    }, [architectureId, itemId, itemType, path, versions, pathFreshAt, versionsFreshAt]);

    return null;
};

export const JobItemVersionRedirect: FunctionComponent<{ readonly entity: EntityIdentifier }> = ({ entity }) => {
    const resolved = resolveEntity(entity);
    if (is.null(resolved)) return null;
    return (
        <RedirectToLatestVersion
            architectureId={resolved.architectureId}
            itemId={resolved.itemId}
            itemType={resolved.itemType}
        />
    );
};
